import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { PageColumn, WaitFor } from '../../components/blocks';
import { Text, useTranslation } from '../../components/blocks/localization';
import { MultiColumnInfoBoxTile } from '../../components/blocks/MultiColumnInfoBoxTile';
import { Tab } from '../../components/blocks/Tab';
import { Tile } from '../../components/composites';
import LabeledField from '../../components/composites/LabeledField';
import { useModal } from '../../components/composites/Modal';
import { PurchaseOrderAttachmentsModal } from '../../components/composites/PurchaseOrderAttachmentsModal';
import { TabGroup } from '../../components/composites/TabGroup';
import { useDownloadSalesOrderDocument } from '../../hooks/useDownloadSalesOrderDocument';
import { useExpandableRows } from '../../hooks/useExpandableRows';
import { useExportSalesOrderDemands } from '../../hooks/useExportSalesOrderDemands';
import { usePurchaseOrderSourceDocument } from '../../hooks/usePurchaseOrderSourceDocument';
import { getResellerDetails, getResellerItems } from '../../redux/actions/resellerActionCreators';
import { getSalesOrderDemandById } from '../../redux/actions/salesOrderDemandActionCreators';
import { RootState, SalesOrderDemand, IAsynchronouslyLoadedValue, Location } from '../../redux/types';
import { findLocationAddress, formatAddress } from '../../utils/addressHelper';
import { formatDateIsoOrBlank } from '../../utils/dateUtils';
import { SUBMITTED_ORDER_QUERY_PARAM } from '../../utils/extractionUtils';
import { getInheritedRoles } from '../../utils/security';
import { AsinTable } from './AsinTable';
import { ShipmentTable } from './ShipmentTable';
import { StickyHeaderStyles } from './StickyHeaderStyles';
import { waitForSalesOrder } from './waitForSalesOrder';

// Lets the page tell a missing order apart from a failed lookup.
const ORDER_NOT_FOUND_ERROR = new Error('Not found');

const EMPTY_VALUE = '—';

const orEmpty = (value?: string) => value || EMPTY_VALUE;

const formatShipToAddress = (locationCode?: string, locations?: Location[]) => {
    const shippingAddress = locationCode && findLocationAddress(locations, locationCode, 'shippingAddress');
    return shippingAddress ? formatAddress(shippingAddress) : EMPTY_VALUE;
};

const getExpandableAsins = (order: SalesOrderDemand) =>
    (order.items || [])
        .filter((item) => (order.shipments || []).some((s) => s.items?.some((si) => si.asin === item.asin)))
        .map((item) => item.asin!);

const PurchaseOrderViewPage: FC<PurchaseOrderViewPageProps> = ({
    resellerItems,
    getResellerItems,
    getResellerDetails,
    resellerDetails,
    resellerId,
    salesOrderDemands,
    locale,
    username,
    userRoles,
}) => {
    const [salesOrder, setSalesOrder] = useState<IAsynchronouslyLoadedValue<SalesOrderDemand>>({
        status: 'Uninitialized',
    });
    const asins = useExpandableRows();
    const shipments = useExpandableRows();
    const isUnmounted = useRef(false);
    const { purchaseOrderId } = useParams<{ purchaseOrderId: string }>();
    const submittedFor = new URLSearchParams(useLocation().search).get(SUBMITTED_ORDER_QUERY_PARAM);
    const isSubmittedOrder = !!resellerId && submittedFor === resellerId;
    const { t } = useTranslation(['purchaseOrderViewPage', 'purchaseOrderListPage', 'common']);
    const { isExporting, handleExport } = useExportSalesOrderDemands();
    const sourceDocument = usePurchaseOrderSourceDocument(resellerId, purchaseOrderId);
    const downloadSalesOrderDocument = useDownloadSalesOrderDocument();
    const modal = useModal();
    const resellerItemsData = resellerItems.value || [];
    const timezone = resellerDetails.value?.resellerIanaTimezone || undefined;

    const canUploadAttachments = useMemo(() => {
        if (!userRoles || userRoles.length === 0) return false;
        if (userRoles.includes('poEditor')) return true;
        return getInheritedRoles(userRoles).has('poEditor');
    }, [userRoles]);

    const combinedLoadState = {
        ...salesOrder,
        status:
            salesOrder.status === 'Loaded' &&
            (resellerDetails.status === 'Uninitialized' || resellerDetails.status === 'Loading')
                ? ('Loading' as const)
                : salesOrder.status,
    };

    const handleExportCallback = useCallback(() => {
        if (!resellerId) return;
        handleExport(resellerId, { purchaseOrderIds: [purchaseOrderId] }, `${purchaseOrderId}`, locale, 1, timezone);
    }, [resellerId, purchaseOrderId, locale, handleExport, timezone]);

    const handleDownloadSourceDocument = useCallback(() => {
        if (!resellerId || !sourceDocument) return;
        downloadSalesOrderDocument(resellerId, purchaseOrderId, sourceDocument.id);
    }, [resellerId, purchaseOrderId, sourceDocument, downloadSalesOrderDocument]);

    const openAttachmentsModal = useCallback(
        (shipmentId: string) => {
            if (!resellerId || !purchaseOrderId) return;
            modal.prompt({
                title: t('attachments-modalTitle', { shipmentId }),
                content: () => (
                    <PurchaseOrderAttachmentsModal
                        purchaseOrderId={purchaseOrderId}
                        shipmentId={shipmentId}
                        resellerId={resellerId}
                        username={username ?? ''}
                    />
                ),
            });
        },
        [resellerId, purchaseOrderId, username, modal, t]
    );

    useEffect(() => {
        if (resellerId && resellerItems.status === 'Uninitialized') {
            getResellerItems(resellerId);
        }
    }, [resellerId, resellerItems.status, getResellerItems]);

    useEffect(() => {
        if (resellerId && resellerDetails.status === 'Uninitialized') {
            getResellerDetails(resellerId);
        }
    }, [resellerId, resellerDetails.status, getResellerDetails]);

    useEffect(
        () => () => {
            isUnmounted.current = true;
        },
        []
    );

    useEffect(() => {
        if (resellerId && purchaseOrderId && salesOrder.status === 'Uninitialized') {
            const cached = salesOrderDemands.value?.find((d) => d.purchaseOrderId === purchaseOrderId);
            if (cached) {
                setSalesOrder({ status: 'Loaded', value: cached });
                return;
            }
            // Only a link from a submitted extraction waits, so a missing order is still reported right away.
            const findSalesOrder = async () => {
                const order = await getSalesOrderDemandById(resellerId, purchaseOrderId);
                if (order || !isSubmittedOrder) return order;
                return waitForSalesOrder(resellerId, purchaseOrderId, () => isUnmounted.current);
            };
            setSalesOrder({ status: 'Loading' });
            findSalesOrder()
                .then((data) => {
                    if (data) {
                        setSalesOrder({ status: 'Loaded', value: data });
                    } else {
                        setSalesOrder({ status: 'Failed', error: ORDER_NOT_FOUND_ERROR });
                    }
                })
                .catch((error) => setSalesOrder({ status: 'Failed', error }));
        }
    }, [resellerId, purchaseOrderId, salesOrder.status, salesOrderDemands.value, isSubmittedOrder]);

    useEffect(() => {
        if (salesOrder.status === 'Loaded' && salesOrder.value) {
            asins.setExpanded(getExpandableAsins(salesOrder.value));
            shipments.setExpanded((salesOrder.value.shipments || []).map((s) => s.shipmentId!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesOrder.status, salesOrder.value]);
    return (
        <PageColumn width={'100%'} spacing={'medium'} data-testid={'purchaseOrderViewPage'}>
            <StickyHeaderStyles />
            <h2>{t('purchaseOrder-pageTitle')}</h2>
            <WaitFor
                lazyValue={combinedLoadState}
                failedPlaceholder={
                    salesOrder.error === ORDER_NOT_FOUND_ERROR && (
                        <Text data-testid={'purchaseOrderViewPage.orderNotFound'}>
                            {t('orderNotFound-message', { purchaseOrderId })}
                        </Text>
                    )
                }
            >
                {(order) => (
                    <>
                        <MultiColumnInfoBoxTile
                            widths={'grid-4 grid-4 grid-4'}
                            title={t('purchaseOrderId-label', {
                                purchaseOrderId: order.purchaseOrderId,
                            })}
                            actionButtons={[
                                ...(sourceDocument
                                    ? [
                                          {
                                              id: 'download-purchase-order-document',
                                              title: t('downloadPurchaseOrderDocument-button'),
                                              callback: handleDownloadSourceDocument,
                                              type: 'secondary' as const,
                                          },
                                      ]
                                    : []),
                                {
                                    id: 'export-to-xlsx',
                                    title: t('exportToXlsx-button'),
                                    callback: handleExportCallback,
                                    isBusy: isExporting,
                                    isDisabled: !purchaseOrderId,
                                    type: 'primary',
                                },
                            ]}
                        >
                            <>
                                <LabeledField label={t('status-label')} text={t.cognate(orEmpty(order.status))} />
                                <LabeledField
                                    label={t('creationDate-label')}
                                    text={t.cognate(orEmpty(formatDateIsoOrBlank(order.creationDate, timezone)))}
                                />
                            </>
                            <>
                                <LabeledField
                                    label={t('requestedShipDate-label')}
                                    text={t.cognate(orEmpty(formatDateIsoOrBlank(order.requestedShipDate, timezone)))}
                                />
                                <LabeledField
                                    label={t('requestedDeliveryDate-label')}
                                    text={t.cognate(
                                        orEmpty(formatDateIsoOrBlank(order.requestedDeliveryDate, timezone))
                                    )}
                                />
                            </>
                            <>
                                <LabeledField
                                    label={t('shipToLocationCode-label')}
                                    text={t.cognate(orEmpty(order.shipToLocationCode))}
                                />
                                <LabeledField
                                    label={t('shipToAddress-label')}
                                    text={t.cognate(
                                        formatShipToAddress(
                                            order.shipToLocationCode,
                                            resellerDetails.value?.resellerLocations?.locations
                                        )
                                    )}
                                />
                            </>
                        </MultiColumnInfoBoxTile>

                        <Tile title={t('details-title')} width={'100%'} contentSpacingInset={'small'}>
                            <TabGroup initialTabId={'asinView'}>
                                <Tab
                                    tabId={'asinView'}
                                    label={t('byAsin-tab', { count: (order.items || []).length })}
                                    dataTestId={'poViewAsinTab'}
                                >
                                    <AsinTable
                                        items={order.items || []}
                                        expandedAsins={asins.expanded}
                                        toggleAsin={asins.toggle}
                                        resellerItems={resellerItemsData}
                                        shipments={order.shipments || []}
                                        expandAllAsins={() => asins.expandAll(getExpandableAsins(order))}
                                        collapseAllAsins={asins.collapseAll}
                                        requestedShipDate={order.requestedShipDate}
                                        requestedDeliveryDate={order.requestedDeliveryDate}
                                        totals={{
                                            ordered: order.totalUnitsOrdered,
                                            open: order.totalUnitsOpen,
                                            released: order.totalUnitsReleased,
                                            shipped: order.totalUnitsShipped,
                                            canceled: order.totalUnitsCanceled,
                                        }}
                                        t={t}
                                        timezone={timezone}
                                    />
                                </Tab>
                                <Tab
                                    tabId={'shipmentsView'}
                                    dataTestId={'poViewShipmentsTab'}
                                    label={t('byShipment-tab', {
                                        count: (order.shipments || []).length,
                                    })}
                                    disabled={(order.shipments || []).length === 0}
                                >
                                    <ShipmentTable
                                        shipmentDetails={order.shipments || []}
                                        expandedShipments={shipments.expanded}
                                        toggleShipment={shipments.toggle}
                                        resellerItems={resellerItemsData}
                                        expandAllShipments={() =>
                                            shipments.expandAll((order.shipments || []).map((s) => s.shipmentId!))
                                        }
                                        collapseAllShipments={shipments.collapseAll}
                                        requestedShipDate={order.requestedShipDate}
                                        requestedDeliveryDate={order.requestedDeliveryDate}
                                        totalUnits={order.totalUnitsShipped}
                                        t={t}
                                        timezone={timezone}
                                        canUploadAttachments={canUploadAttachments}
                                        onUploadAttachments={openAttachmentsModal}
                                    />
                                </Tab>
                            </TabGroup>
                        </Tile>
                    </>
                )}
            </WaitFor>
        </PageColumn>
    );
};

const mapStateToProps = ({ resellerReducer, salesOrderDemandReducer, userReducer }: RootState) => ({
    resellerItems: resellerReducer.resellerItems,
    resellerDetails: resellerReducer.resellerDetails,
    resellerId: resellerReducer.selectedReseller.value?.resellerId,
    salesOrderDemands: salesOrderDemandReducer.salesOrderDemandData,
    locale: userReducer.user?.locale,
    username: userReducer.user?.username,
    userRoles: userReducer.user?.roles,
});

const mapDispatchToProps = {
    getResellerItems,
    getResellerDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PurchaseOrderViewPageProps = ConnectedProps<typeof connector>;
export default connector(PurchaseOrderViewPage);
